import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { UnitEditHeader } from '@/components/admin/UnitEditHeader';
import { UnitEditForm } from '@/components/admin/UnitEditForm';
import { UnitEditPreview } from '@/components/admin/UnitEditPreview';
import { useUnitEdit } from '@/hooks/useUnitEdit';
import { getArchitectureDrawingForUnit, type ProcessedArchitectureDrawing } from '@/lib/architecture-drawings';
import type { Tables } from '@/integrations/supabase/types';

type PropertyUnit = Tables<'property_units'>;

export default function AdminUnitEdit() {
  const { id } = useParams<{ id: string }>();
  const [unit, setUnit] = useState<PropertyUnit | null>(null);
  const [isLoadingUnit, setIsLoadingUnit] = useState(true);
  const [architectureDrawing, setArchitectureDrawing] = useState<ProcessedArchitectureDrawing | null>(null);

  const {
    formData,
    isSaving,
    hasUnsavedChanges,
    saveStatus,
    updateFormData,
    handleSave,
    handleCancel,
  } = useUnitEdit(unit);

  // Load unit data
  useEffect(() => {
    const loadUnit = async () => {
      if (!id) return;

      setIsLoadingUnit(true);
      try {
        const { data, error } = await supabase
          .from('property_units')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setUnit(data);

        // Load architecture drawing based on unit_type
        if (data.unit_type) {
          const archDrawing = await getArchitectureDrawingForUnit(data);
          setArchitectureDrawing(archDrawing);
        } else {
          setArchitectureDrawing(null);
        }
      } catch (error) {
        console.error('Error loading unit:', error);
      } finally {
        setIsLoadingUnit(false);
      }
    };

    loadUnit();
  }, [id]);

  // Prevent navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      // Escape to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleCancel]);

  if (isLoadingUnit) {
    return (
      <div className="min-h-screen bg-background">
        <UnitEditHeader
          unit={null}
          isSaving={false}
          hasUnsavedChanges={false}
          saveStatus="saved"
          isPublished={true}
          onPublishChange={() => {}}
          onSave={() => {}}
          onCancel={handleCancel}
        />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Loading unit...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="min-h-screen bg-background">
        <UnitEditHeader
          unit={null}
          isSaving={false}
          hasUnsavedChanges={false}
          saveStatus="saved"
          isPublished={true}
          onPublishChange={() => {}}
          onSave={() => {}}
          onCancel={handleCancel}
        />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Unit not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UnitEditHeader
        unit={unit}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        saveStatus={saveStatus}
        isPublished={formData.is_published}
        onPublishChange={(published) => updateFormData('is_published', published)}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Form */}
          <div className="lg:col-span-7">
            <UnitEditForm
              unit={unit}
              formData={formData}
              onUpdate={updateFormData}
              architectureDrawing={architectureDrawing}
            />
          </div>

          {/* Right Column: Preview */}
          <div className="lg:col-span-5">
            <UnitEditPreview unit={unit} formData={formData} />
          </div>
        </div>
      </div>
    </div>
  );
}
